import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { User } from 'firebase/auth'
import { onAuthChange, getUserData, getCurrentUser } from '../services/authService'
import { clearAllOfflineData } from '../services/offlineSyncService'
import { ensureCompanySettingsInCloud, hydrateCompanySettingsFromCloud } from '../services/settingsService'
import type { UserData } from '../services/authService'
import { getOrCreateSubscription, updateSubscriptionStatus, getSubscriptionState } from '../services/subscriptionService'
import type { CompanySubscription, SubscriptionState } from '../types'
import { isFirebaseConfigured } from '../services/firebase'
import { clearCurrentUserSnapshot, setCurrentUserSnapshot } from '../utils/authUtils'
import {
  CUSTOMER_UNLOCKED_SUBSCRIPTION_STATE,
  isCustomerSubscriptionBypassEnabled
} from '../config/customerBuild'

interface AuthContextType {
  user: User | null
  userData: UserData | null
  isLoading: boolean
  isAuthenticated: boolean
  updateUserData: (updates: Partial<UserData>) => void
  // Subscription
  subscription: CompanySubscription | null
  subscriptionState: SubscriptionState
  refreshSubscription: () => Promise<void>
  // Login notification state
  showSubscriptionNotification: boolean
  dismissSubscriptionNotification: () => void
}

// Default subscription state
const defaultSubscriptionState: SubscriptionState = {
  isActive: false,
  isTrialing: false,
  isExpired: true,
  isGracePeriod: false,
  daysRemaining: 0,
  canEdit: false,
  showExpiryWarning: false,
  subscription: null
}

const demoSubscriptionState: SubscriptionState = {
  isActive: true,
  isTrialing: false,
  isExpired: false,
  isGracePeriod: false,
  daysRemaining: 365,
  canEdit: true,
  showExpiryWarning: false,
  subscription: null
}

const isDemoAuthMode = () => !isFirebaseConfigured()

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  isLoading: true,
  isAuthenticated: false,
  updateUserData: () => {},
  subscription: null,
  subscriptionState: defaultSubscriptionState,
  refreshSubscription: async () => {},
  showSubscriptionNotification: false,
  dismissSubscriptionNotification: () => {}
})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userData, setUserData] = useState<UserData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [subscription, setSubscription] = useState<CompanySubscription | null>(null)
  const [subscriptionState, setSubscriptionState] = useState<SubscriptionState>(defaultSubscriptionState)
  const [showSubscriptionNotification, setShowSubscriptionNotification] = useState(false)

  const applyUnlockedSubscriptionAccess = useCallback(() => {
    setSubscription(null)
    setSubscriptionState(CUSTOMER_UNLOCKED_SUBSCRIPTION_STATE)
    setShowSubscriptionNotification(false)
  }, [])

  const updateUserData = useCallback((updates: Partial<UserData>) => {
    setUserData((prev) => {
      if (!prev) return prev
      const next = { ...prev, ...updates }
      setCurrentUserSnapshot(next)
      localStorage.setItem('user', JSON.stringify(next))
      return next
    })
  }, [])

  // Load subscription for a company
  const loadSubscription = useCallback(async (companyId: string, role?: string) => {
    if (isDemoAuthMode()) {
      setSubscription(null)
      setSubscriptionState(demoSubscriptionState)
      setShowSubscriptionNotification(false)
      return
    }

    if (isCustomerSubscriptionBypassEnabled) {
      applyUnlockedSubscriptionAccess()
      return
    }

    try {
      // Get or create subscription (creates trial if none exists)
      let sub = await getOrCreateSubscription(companyId)

      // Update status based on dates (check if expired)
      sub = await updateSubscriptionStatus(sub)

      setSubscription(sub)

      // Calculate subscription state
      const state = getSubscriptionState(sub, role)
      setSubscriptionState(state)

      // Check if we should show notification (< 5 days remaining)
      // Only show once per session (check localStorage)
      const today = new Date().toISOString().split('T')[0]
      const lastNotificationDate = localStorage.getItem(`sub_notification_${companyId}`)

      if (state.showExpiryWarning && lastNotificationDate !== today) {
        setShowSubscriptionNotification(true)
      }
    } catch (error) {
      console.error('Error loading subscription:', error)
      setSubscription(null)
      setSubscriptionState(defaultSubscriptionState)
    }
  }, [applyUnlockedSubscriptionAccess])

  // Refresh subscription (can be called from other components)
  const refreshSubscription = useCallback(async () => {
    if (isDemoAuthMode()) {
      setSubscription(null)
      setSubscriptionState(demoSubscriptionState)
      setShowSubscriptionNotification(false)
      return
    }

    if (isCustomerSubscriptionBypassEnabled) {
      applyUnlockedSubscriptionAccess()
      return
    }

    if (userData?.companyId) {
      await loadSubscription(userData.companyId, userData.role)
    }
  }, [applyUnlockedSubscriptionAccess, userData?.companyId, userData?.role, loadSubscription])

  // Dismiss notification (stores in localStorage so it doesn't show again today)
  const dismissSubscriptionNotification = useCallback(() => {
    setShowSubscriptionNotification(false)
    if (userData?.companyId) {
      const today = new Date().toISOString().split('T')[0]
      localStorage.setItem(`sub_notification_${userData.companyId}`, today)
    }
  }, [userData?.companyId])

  useEffect(() => {
    const demoModeAtStart = isDemoAuthMode()

    let settled = false
    const loadingFallback = window.setTimeout(() => {
      if (!settled && demoModeAtStart) {
        console.warn('Demo auth loading timeout: continuing with local auth state')
        setIsLoading(false)
      }
    }, 8000)

    // In Firebase mode users/{uid} is the source of truth for company access.
    // Do not preload cached user data, because a stale companyId can start
    // Firestore listeners before the real auth/profile state is ready.
    if (demoModeAtStart) {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser) as UserData
          setUserData(parsedUser)
          setCurrentUserSnapshot(parsedUser)
        } catch (error) {
          console.error('Failed to parse stored user data:', error)
        }
      }
    }

    // Listen to auth state changes
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      const demoMode = isDemoAuthMode()

      try {
        setUser(firebaseUser)

        if (firebaseUser) {
          if (demoMode) {
            const storedUserRaw = localStorage.getItem('user')
            if (storedUserRaw) {
              try {
                const storedUser = JSON.parse(storedUserRaw) as UserData
                setUserData(storedUser)
                setCurrentUserSnapshot(storedUser)
                setSubscription(null)
                setSubscriptionState(demoSubscriptionState)
                setShowSubscriptionNotification(false)
              } catch (error) {
                console.error('Failed to parse demo user data:', error)
                setUserData(null)
                clearCurrentUserSnapshot()
                localStorage.removeItem('user')
              }
            } else {
              setUserData(null)
              clearCurrentUserSnapshot()
              setSubscription(null)
              setSubscriptionState(defaultSubscriptionState)
            }

            return
          }

          const storedUserRaw = localStorage.getItem('user')
          if (storedUserRaw) {
            try {
              const storedUser = JSON.parse(storedUserRaw)
              if (storedUser?.uid && storedUser.uid !== firebaseUser.uid) {
                await clearAllOfflineData()
              }
            } catch {
              // Ignore parsing errors
            }
          }

          // Get user data from Firestore
          const data = await getUserData(firebaseUser.uid)
          const mappedCompanyId = data?.companyId || (data as any)?.businessId || null
          if (data && mappedCompanyId) {
            const normalizedData = { ...data, companyId: mappedCompanyId }
            console.info('[Auth Firebase Debug] User company mapping loaded', {
              uid: firebaseUser.uid,
              userDocPath: `users/${firebaseUser.uid}`,
              userDocData: data,
              companyId: normalizedData.companyId,
              businessId: (data as any).businessId || null
            })
            setUserData(normalizedData)
            setCurrentUserSnapshot(normalizedData)
            localStorage.setItem('user', JSON.stringify(normalizedData))

            // Load subscription for the company
            void hydrateCompanySettingsFromCloud(mappedCompanyId)
            if (['admin', 'org_admin', 'super_admin'].includes(data.role)) {
              void ensureCompanySettingsInCloud(mappedCompanyId)
            }
            if (isCustomerSubscriptionBypassEnabled) {
              applyUnlockedSubscriptionAccess()
            } else {
              await loadSubscription(mappedCompanyId, data.role)
            }
          } else {
            console.warn('[Auth] User data missing companyId/businessId - clearing stored user', {
              uid: firebaseUser.uid,
              userDocPath: `users/${firebaseUser.uid}`,
              userDocData: data
            })
            setUserData(null)
            clearCurrentUserSnapshot()
            localStorage.removeItem('user')
          }
        } else {
          setUserData(null)
          clearCurrentUserSnapshot()
          setSubscription(null)
          setSubscriptionState(defaultSubscriptionState)
          setShowSubscriptionNotification(false)
          localStorage.removeItem('user')
          if (!demoMode) {
            await clearAllOfflineData()
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        setUserData(null)
        clearCurrentUserSnapshot()
        setSubscription(null)
        setSubscriptionState(defaultSubscriptionState)
      } finally {
        settled = true
        window.clearTimeout(loadingFallback)
        setIsLoading(false)
      }
    })

    return () => {
      settled = true
      window.clearTimeout(loadingFallback)
      unsubscribe()
    }
  }, [applyUnlockedSubscriptionAccess, loadSubscription])

  const value: AuthContextType = {
    user,
    userData,
    isLoading,
    isAuthenticated: !!user,
    updateUserData,
    subscription,
    subscriptionState,
    refreshSubscription,
    showSubscriptionNotification,
    dismissSubscriptionNotification
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
